import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { fetchdropdownTenantHO } from "../services/dropdown";
import { saveConsentNew } from "../services/moka"

interface ConnectModalProps {
  outlet: any; // MokaOutletItem
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after a successful connect, supplying the selected tenantId.
   */
  onConnected: (tenantId: number) => void;
  code: string
}

export const ConnectModal: React.FC<ConnectModalProps> = ({ outlet, isOpen, onClose, onConnected  }) => {
  const [tenants, setTenants] = useState<Array<{ id: number; label: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [selectedTenantLabel, setSelectedTenantLabel] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // Searchable dropdown state
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Animation state
  const [visible, setVisible] = useState<boolean>(false);
  const [animating, setAnimating] = useState<boolean>(false);

  // Fetch tenants when the modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetch = async () => {
      try {
        const data = await fetchdropdownTenantHO();
        const formatted = (data?.dropdownClassDatas ?? []).map((t: any) => ({
          id: t.DataId,
          label: t.DataLabel,
        }));
        setTenants(formatted);
      } catch (err) {
        await Swal.fire({ title: "Error", text: "Failed to load tenant list.", icon: "error" });
      }
    };
    fetch();
  }, [isOpen]);

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      setAnimating(true);
      // Small delay so the initial transform is painted before we transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setAnimating(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [dropdownOpen]);

  const filteredTenants = tenants.filter((t) =>
    t.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectTenant = (id: number, label: string) => {
    setSelectedTenantId(String(id));
    setSelectedTenantLabel(label);
    setDropdownOpen(false);
    setSearchQuery("");
  };

  const handleSave = async () => {
    if (!selectedTenantId || !outlet) return;
    setLoading(true);
    try {
      const code        = sessionStorage.getItem("moka_code") ?? "";
      const accessToken = sessionStorage.getItem("moka_access_token") ?? "";
      const refreshToken= sessionStorage.getItem("moka_refresh_token") ?? "";
      const createdAt   = Number(sessionStorage.getItem("moka_created_token") ?? 0);
      const expiresIn   = Number(sessionStorage.getItem("moka_expired_token") ?? 0);
      const password    = sessionStorage.getItem("moka_password") ?? "";

      const response = await saveConsentNew({
        TenantId:     Number(selectedTenantId),
        Code:         code,         // ✅ dari sessionStorage
        OutletName:   outlet.OutletName,
        Address:      outlet.Address,
        PhoneNumber:  outlet.PhoneNumber || "",
        City:         outlet.City || "",
        Province:     outlet.Province || "",
        PostalCode:   outlet.PostalCode || "",
        MokaOutletID: outlet.OutletId,
        AccessToken:  accessToken,  // ✅ dari sessionStorage
        RefreshToken: refreshToken, // ✅ dari sessionStorage
        PasswordAPI:  password,
        CreatedAt:    createdAt,    // ✅ dari sessionStorage
        ExpiresIn:    expiresIn,    // ✅ dari sessionStorage
      });

      if (response?.Code === 0) {
        await Swal.fire({ title: "Connected", text: "Outlet connected successfully.", icon: "success" });
        onConnected(Number(selectedTenantId));
      } else {
        throw new Error(response?.Message || "Update failed");
      }
    } catch (err: any) {
      const message = err.response?.data?.Message || err.message || "Failed to connect outlet.";
      await Swal.fire({ title: "Error", text: message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!animating && !isOpen) return null;

  return (
    <>
      {/* Inline keyframe styles */}
      <style>{`
        @keyframes cmSlideDown {
          from { opacity: 0; transform: translateY(-40px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes cmSlideUp {
          from { opacity: 1; transform: translateY(0)     scale(1);    }
          to   { opacity: 0; transform: translateY(-40px) scale(0.97); }
        }
        .cm-backdrop-enter  { animation: none; opacity: 0; transition: opacity 0.3s ease; }
        .cm-backdrop-visible { opacity: 1; }
        .cm-box-enter  { animation: cmSlideDown 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .cm-box-exit   { animation: cmSlideUp   0.3s  ease-in forwards; }
      `}</style>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-50 p-4 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Modal — positioned in upper-third of screen */}
        <div className="flex justify-center pt-[12vh]">
          <div
            className={visible ? "cm-box-enter" : "cm-box-exit"}
            style={{ width: "100%", maxWidth: "24rem" }}
          >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/50 ring-1 ring-black/5">
              {/* Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.826L10.242 14.172a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101m-.001-4.074a4 4 0 01-5.656 0" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 tracking-tight">Connect Outlet</h2>
              </div>

              <div className="space-y-4">
                {/* Searchable Tenant Dropdown */}
                <div>
                  <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Select Tenant
                  </label>
                  <div ref={dropdownRef} className="relative">
                    {/* Trigger button */}
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((o) => !o)}
                      className="w-full bg-white/50 border border-gray-200 rounded-xl p-3 text-sm text-left focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all flex items-center justify-between gap-2"
                    >
                      <span className={selectedTenantLabel ? "text-gray-700 font-medium" : "text-gray-400"}>
                        {selectedTenantLabel || "-- Choose Tenant --"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown panel */}
                    {dropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                            </svg>
                            <input
                              ref={searchInputRef}
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Search tenant..."
                              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                          </div>
                        </div>

                        {/* Options list */}
                        <ul className="max-h-44 overflow-y-auto py-1">
                          {filteredTenants.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-gray-400 text-center">No tenants found</li>
                          ) : (
                            filteredTenants.map((t) => (
                              <li
                                key={t.id}
                                onClick={() => handleSelectTenant(t.id, t.label)}
                                className={`px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-blue-50 hover:text-blue-700 ${
                                  String(t.id) === selectedTenantId ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-700"
                                }`}
                              >
                                {t.label}
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!selectedTenantId || loading}
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : null}
                    {loading ? "Saving..." : "Connect Now"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
